import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Mail, Award, LockKeyhole } from "lucide-react";
import { company } from "../data/content";
import { useCmsList, useCmsSingleton } from "../hooks/useCmsContent";
import BrandLogo from "./BrandLogo";

const Logo = () => (
  <Link to="/" className="flex items-center py-2.5" data-testid="logo-link">
    <BrandLogo full />
  </Link>
);

const nav = [
  { label: "About", to: "/about", children: [
    { label: "About Us", to: "/about" },
    { label: "Facilities — Tarapur Works", to: "/facilities" },
  ]},
  { label: "Products", to: "/products" },
  { label: "Services", to: "/services" },
  { label: "Capabilities", to: "/capabilities" },
  { label: "Projects", to: "/projects" },
  { label: "Quality", to: "/quality" },
  { label: "Certifications", to: "/certifications" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(null);
  const location = useLocation();
  const cmsNav = useCmsList("nav_item", []);
  const navMeta = useCmsSingleton("navigation_meta", {});
  const ctaText = navMeta.cta_text || "Enquire Now";
  const ctaLink = navMeta.cta_link || "/contact";
  const navItems = useMemo(() => {
    const header = cmsNav.filter((i) => i.area === "header");
    if (!header.length) return nav;
    return header
      .filter((i) => !i.parent)
      .map((t) => {
        const children = header.filter((c) => c.parent === t.id).map((c) => ({ label: c.label, to: c.to }));
        return children.length ? { label: t.label, to: t.to, children } : { label: t.label, to: t.to };
      });
  }, [cmsNav]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMobileSub(null); }, [location]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <div className={`bg-[#0A1128] text-slate-300 overflow-hidden transition-all duration-300 ${scrolled ? "max-h-0" : "max-h-12"}`}>
        <div className="container-gre flex items-center justify-between gap-3 py-2 text-[10px] sm:text-[11px] font-mono-tech tracking-wider">
          <span className="hidden min-[480px]:flex items-center gap-2 uppercase whitespace-nowrap" data-testid="topbar-cert">
            <Award size={12} className="text-[#C5221F]" /> ASME 'U' &amp; 'U2' Stamp Holder
          </span>
          <span className="flex items-center gap-2.5 sm:gap-3 whitespace-nowrap">
            <a href={`mailto:${company.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors duration-200" data-testid="topbar-email"><Mail size={12} /> {company.email}</a>
            <span className="text-slate-600" aria-hidden="true">|</span>
            <Link to="/app" className="flex items-center gap-1.5 hover:text-white transition-colors duration-200 uppercase" data-testid="topbar-staff-login"><LockKeyhole size={12} className="text-[#C5221F]" /> Staff Login</Link>
          </span>
        </div>
      </div>

      <div className={`border-b border-slate-200 transition-all duration-300 ${scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_10px_36px_-14px_rgba(10,17,40,0.35)]" : "bg-white/95 backdrop-blur-xl"}`}>
        <div className="container-gre flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden xl:flex items-center gap-7" data-testid="desktop-nav">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label} className="relative group" data-testid={`nav-${item.label.toLowerCase()}-dropdown`}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `link-underline ${isActive ? "active" : ""} flex items-center gap-1 py-5 text-[13px] font-semibold uppercase tracking-wider text-slate-700 hover:text-[#0A1128] transition-colors duration-200`
                    }
                  >
                    {item.label} <ChevronDown size={13} className="transition-transform duration-300 group-hover:rotate-180" />
                  </NavLink>
                  <div className="absolute left-0 top-full pt-0 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
                    <div className="bg-white border border-slate-200 border-t-2 border-t-[#C5221F] shadow-xl min-w-[260px] py-2">
                      {item.children.map((c) => (
                        <Link key={c.label} to={c.to} className="block px-5 py-2.5 text-[13px] font-medium text-slate-600 hover:text-[#C5221F] hover:bg-slate-50 transition-colors duration-150">
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  className={({ isActive }) =>
                    `link-underline ${isActive ? "active" : ""} py-5 text-[13px] font-semibold uppercase tracking-wider text-slate-700 hover:text-[#0A1128] transition-colors duration-200`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to={ctaLink}
              data-testid="enquire-now-btn"
              className="hidden sm:inline-flex items-center bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-6 py-3 text-xs uppercase tracking-widest transition-colors duration-300"
            >
              {ctaText}
            </Link>
            <button
              className="xl:hidden p-2 text-slate-800"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open-btn"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </div>

      <div className={`fixed inset-0 z-[60] xl:hidden transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} data-testid="mobile-menu">
        <div className="absolute inset-0 bg-[#0A1128]/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-[86%] max-w-sm bg-[#0A1128] text-white flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <span className="bg-white rounded-sm px-3 py-2" data-testid="mobile-menu-logo">
              <BrandLogo full imgClass="h-9" />
            </span>
            <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close-btn" className="p-2"><X size={24} /></button>
          </div>
          <nav className="flex-1 overflow-y-auto p-5 space-y-1">
            {navItems.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    className="w-full flex items-center justify-between py-3.5 font-display font-semibold uppercase tracking-wider text-sm border-b border-white/5"
                    onClick={() => setMobileSub(mobileSub === item.label ? null : item.label)}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}-toggle`}
                  >
                    {item.label}
                    <ChevronDown size={16} className={`transition-transform duration-300 ${mobileSub === item.label ? "rotate-180" : ""}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${mobileSub === item.label ? "max-h-96" : "max-h-0"}`}>
                    {item.children.map((c) => (
                      <Link key={c.label} to={c.to} className="block pl-4 py-2.5 text-sm text-slate-300 hover:text-[#C5221F] transition-colors duration-150">{c.label}</Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link key={item.label} to={item.to} className="block py-3.5 font-display font-semibold uppercase tracking-wider text-sm border-b border-white/5 hover:text-[#C5221F] transition-colors duration-150">
                  {item.label}
                </Link>
              )
            )}
          </nav>
          <div className="p-5 border-t border-white/10 space-y-3">
            <Link to="/app" data-testid="mobile-staff-login-btn" className="flex items-center justify-center gap-2 border border-white/25 hover:border-white font-display font-semibold px-6 py-4 text-xs uppercase tracking-widest transition-colors duration-300">
              <LockKeyhole size={13} /> Staff Login
            </Link>
            <Link to={ctaLink} data-testid="mobile-enquire-btn" className="block text-center bg-[#C5221F] hover:bg-[#A81B18] font-display font-semibold px-6 py-4 text-xs uppercase tracking-widest transition-colors duration-300">
              {ctaText}
            </Link>
            <p className="mt-4 text-center font-mono-tech text-[11px] text-slate-400">{company.phone} · {company.email}</p>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
